import { action, computed, makeObservable, observable } from "mobx";
import { FileUpload, UploadStatus } from "./FileUpload";
import _ from "lodash";
import { PatientToUpload } from "./PatientToUpload";
import JSZip from "jszip";
import { notNullOrUndefined } from "../util/functions/notNullOrUndefined";
import { nullOrUndefined } from "../util/functions/nullOrUndefined";
import type { IUploadCodeAndSourceSelectionStore } from "./content/upload-code-and-source-selection/UploadCodeAndSourceSelectionStore";
import type { IPatientUploadCompleteCallbackStore } from "./PatientUploadCompleteCallbackStore";
import type { IDicomUploadTusFingerprintStore } from "./DicomUploadTusFingerprintStore";

export interface IUploadStore {
    filesNotUploadable: FileUpload[];
    patientsFound: PatientToUpload[];
    numberOfSelectedFiles: number;
    numberOfCheckingFiles: number;
    numberOfDICOMFiles: number;
    preventInputOfNewFiles: boolean;
    allUploadsDone: boolean;
    somethingIsUploading: boolean;
    maxUploadSize: number;
    maxUploadFilesAtOnce: number;

    addSelectedFiles(files: File[]): void;

    resetFiles(): void;

    removePatientToUpload(patientToUpload: PatientToUpload): void;
}

export class UploadStore implements IUploadStore {
    private readonly endpoint = "/POW4/files";

    public maxUploadSize: number = 1024 * 1024 * 1024; //1GB

    public maxUploadFilesAtOnce = 4;

    constructor(
        private readonly uploadCodeAndSourceSelectionStore: IUploadCodeAndSourceSelectionStore,
        private readonly patientUploadCompleteCallbackStore: IPatientUploadCompleteCallbackStore,
        private readonly dicomUploadTusFingerprintStore: IDicomUploadTusFingerprintStore,
    ) {
        makeObservable(this);
    }

    @action
    public async addSelectedFiles(files: File[], overrulePreventInput = false): Promise<void> {
        if (this.preventInputOfNewFiles && !overrulePreventInput) return;

        //Read files
        for (let i = files.length - 1; i >= 0; i--) {
            //Check if zip file
            if (files[i].name.endsWith(".zip")) {
                await this.readZipFile(files[i]);
            }
            //Not a zip file, continue
            else {
                const fileUpload = this.makeNewSingleFileUpload(files[i]);
                this.addFileToCheck(fileUpload);
            }
        }

        await this.checkFilesToCheck();
        this.checkPatientDataUploadableFiles();
    }

    private async readZipFile(file: File): Promise<void> {
        try {
            const zip = new JSZip();

            //Load files in zip
            const loadedZipFile = await zip.loadAsync(file);

            //Wait for unzip of zipfiles and filter out directories (null) or files that were unable to unzip
            const waitForUnzip = Object.keys(loadedZipFile.files)
                .map((fileName) => zip.file(fileName))
                .filter(notNullOrUndefined)
                .map(
                    async (file) => {
                        try {
                            const blob = await file.async("blob");
                            return new File([blob], file.name);
                        } catch (e) {
                            console.error(`❌ Could not unzip ${file.name}.`, e);
                            throw e;
                        }
                    });

            //We got all the files from the zip, try to add them again
            const files = await Promise.all(waitForUnzip);
            return this.addSelectedFiles(files, true);
        } catch (e) {
            //Incorrect zip file, move to notUploadable!
            console.error(e, file);
            const fileUpload = this.makeNewSingleFileUpload(file);
            fileUpload.setChecking(false);
            this.addFileToNotUploadable(fileUpload);
        }
    }

    private async checkFilesToCheck(): Promise<void> {
        //check and await each of the files to check to determine whether or not they are uploadable
        for (let i = this.filesToCheck.length - 1; i >= 0; i--) {
            const fileUpload = this.filesToCheck[i];
            await fileUpload.checkAndMakeFileUploadReady();
            this.removeFileFromFilesToCheck(i);

            if (fileUpload.uploadable) {
                this.addFileToUploadable(fileUpload);
            } else {
                this.addFileToNotUploadable(fileUpload);
            }
        }
    }

    @action
    private checkPatientDataUploadableFiles(): void {
        const groupedFiles = _.groupBy(this.filesUploadable, (f) => f.patientId);

        this.filesUploadable = [];

        for (const key in groupedFiles) {
            const files = groupedFiles[key];
            const patientAlreadyExists = this.patientsFound.find((p) => p.patientId === key);
            if (patientAlreadyExists) {
                patientAlreadyExists.addFiles(files);
            } else {
                this.patientsFound.push(
                    new PatientToUpload(files, this, this.patientUploadCompleteCallbackStore, this.uploadCodeAndSourceSelectionStore, this.dicomUploadTusFingerprintStore),
                );
            }
        }
    }

    @action
    private removeFileFromFilesToCheck(index: number) {
        this.filesToCheck.splice(index, 1);
    }

    @computed
    public get numberOfSelectedFiles(): number {
        return this.filesNotUploadable.length + this.filesUploadable.length + this.filesToCheck.length + this.numberOfFilesInPatients;
    }

    //Will be used to temporarily store files that are ready to be checked in
    //In checkFilesToCheck() they are added to filesNotUploadable or filesUploadable
    @observable
    private filesToCheck: FileUpload[] = [];

    //Will be used to store files that will not be uploaded in
    @observable
    public filesNotUploadable: FileUpload[] = [];

    //Will be used to temporarily store files that are uploadable in
    //In checkFilesUploadable() they will be moved to the correct patients
    @observable
    private filesUploadable: FileUpload[] = [];

    @observable
    public patientsFound: PatientToUpload[] = [];

    @computed
    public get numberOfDICOMFiles(): number {
        return this.numberOfFilesInPatients + this.filesUploadable.length;
    }

    @computed
    private get numberOfFilesInPatients(): number {
        let totalFiles = 0;
        for (const patient of this.patientsFound) {
            totalFiles += patient.filesToUpload.length;
        }
        return totalFiles;
    }

    @computed
    public get numberOfCheckingFiles(): number {
        return this.filesToCheck.length;
    }

    @computed
    public get preventInputOfNewFiles(): boolean {
        if (this.numberOfCheckingFiles > 0) return true; //Still checking selected files
        if (this.patientsFound?.find((s) => s.status !== UploadStatus.NotStarted)) return true; //Already started or higher
        if (!this.uploadCodeAndSourceSelectionStore.uploadCodeAndSourceValid) return true;
        return false;
    }

    @computed
    public get allUploadsDone(): boolean {
        if (this.patientsFound.length > 0) {
            if (!this.patientsFound.find((s) => s.status !== UploadStatus.Done)) {
                return true;
            }
        }
        return false;
    }

    @action
    private addFileToCheck(fileUpload: FileUpload) {
        if (!this.filesToCheck.find((f) => f.file.name === fileUpload.file.name && f.file.size === fileUpload.file.size)) {
            this.filesToCheck.push(fileUpload);
        }
    }

    @action
    private addFileToNotUploadable(fileUpload: FileUpload) {
        if (!this.filesNotUploadable.find((f) => f.file.name === fileUpload.file.name && f.file.size === fileUpload.file.size)) {
            this.filesNotUploadable.push(fileUpload);
        }
    }

    @action
    private addFileToUploadable(fileUpload: FileUpload) {
        if (!this.filesUploadable.find((f) => f.file.name === fileUpload.file.name && f.file.size === fileUpload.file.size)) {
            this.filesUploadable.push(fileUpload);
        }
    }

    @action
    private makeNewSingleFileUpload(file: File): FileUpload {
        const { uploadCodeAndSourceSelectionStore } = this;
        const { selectedSourceForUpload, uploadCode } = uploadCodeAndSourceSelectionStore;

        if (nullOrUndefined(selectedSourceForUpload)) throw new Error("Cannot upload, no source is selected");

        return new FileUpload(file, this.endpoint, this.maxUploadSize, selectedSourceForUpload.id, uploadCode);
    }

    @action
    public resetFiles(): void {
        this.filesNotUploadable = [];
        this.filesUploadable = [];
        this.filesToCheck = [];
        this.patientsFound = [];
    }

    @action
    public removePatientToUpload(patient: PatientToUpload): void {
        this.patientsFound = this.patientsFound.filter((p) => p.patientGuid !== patient.patientGuid);
    }

    @computed
    public get somethingIsUploading(): boolean {
        return this.patientsFound.find((p) => p.status === UploadStatus.Started) !== undefined;
    }
}
